import type { Card } from "@amzn/ads-blink-client";
import type { AsyncStatus } from "@react-hookz/web";
import { useDebouncedEffect } from "@react-hookz/web";
import { useCallback, useEffect, useRef, useState } from "react";
import * as uuid from "uuid";
import { useStore } from "zustand";

import { ContentSearchErrorNotificationMessage } from "../../components/common/notification.components";
import { notificationStore, NotificationType } from "../../store/notification";
import { unifiedFilterStore } from "../../store/unifiedFilters";
import { InsightDeckTemplateTheme } from "../../types/providers";
import { isSuccess } from "../../utils/api.utils";
import {
    getAdvertiserId,
    getMarketplaceId,
    isAccountAgnosticId,
} from "../../utils/permission.utils";
import { logInsightBrowseResults } from "../../utils/telemetry.utils";
import { useGetUnifiedContentsAsync } from "../unifiedContent/useUnifiedContentsAction";

export interface InsightTemplatesLoaderProps {
    isLoaderInView?: boolean;
}

type InsightTemplates = {
    [key in InsightDeckTemplateTheme]: Card[];
};

type TemplateLoadingStates = {
    [key in InsightDeckTemplateTheme]: AsyncStatus;
};

type TemplateCounts = {
    [key in InsightDeckTemplateTheme]: number;
};

const initialInsightTemplates: InsightTemplates = {
    [InsightDeckTemplateTheme.ADVERTISER_GOALS]: [],
    [InsightDeckTemplateTheme.AD_PRODUCTS]: [],
    [InsightDeckTemplateTheme.DOWNSTREAM_IMPACT]: [],
    [InsightDeckTemplateTheme.INDUSTRY_INSIGHTS]: [],
    [InsightDeckTemplateTheme.SEASONAL_EVENTS]: [],
};

const initialLoadingStates: TemplateLoadingStates = {
    [InsightDeckTemplateTheme.ADVERTISER_GOALS]: "not-executed",
    [InsightDeckTemplateTheme.AD_PRODUCTS]: "not-executed",
    [InsightDeckTemplateTheme.DOWNSTREAM_IMPACT]: "not-executed",
    [InsightDeckTemplateTheme.INDUSTRY_INSIGHTS]: "not-executed",
    [InsightDeckTemplateTheme.SEASONAL_EVENTS]: "not-executed",
};

const initialTemplateCounts: TemplateCounts = {
    [InsightDeckTemplateTheme.ADVERTISER_GOALS]: 0,
    [InsightDeckTemplateTheme.AD_PRODUCTS]: 0,
    [InsightDeckTemplateTheme.DOWNSTREAM_IMPACT]: 0,
    [InsightDeckTemplateTheme.INDUSTRY_INSIGHTS]: 0,
    [InsightDeckTemplateTheme.SEASONAL_EVENTS]: 0,
};

export function useInsightTemplatesLoader({
    isLoaderInView,
}: InsightTemplatesLoaderProps): [
    TemplateLoadingStates,
    InsightTemplates,
    TemplateCounts,
] {
    const [contents, setContents] = useState<InsightTemplates>(
        initialInsightTemplates,
    );
    const [templateLoadingStates, setTemplateLoadingStates] =
        useState<TemplateLoadingStates>(initialLoadingStates);
    const [templateCounts, setTemplateCounts] = useState<TemplateCounts>(
        initialTemplateCounts,
    );
    const pageNumber = useRef(1);

    const [
        unifiedFiltersV2,
        searchQueryV2,
        filterContentStateIsDirty,
        setIsContentDirty,
        setIsMetadataDirty,
        setLastSearchParameters,
        setLastSearchResultTotalCount,
    ] = useStore(unifiedFilterStore, (state) => [
        state.filters,
        state.searchQuery,
        state.isContentDirty,
        state.setIsContentDirty,
        state.setIsMetadataDirty,
        state.setLastSearchParameters,
        state.setLastSearchResultTotalCount,
    ]);

    const searchId = useRef<string>(uuid.v4());
    const searchErrors = useRef<string[]>([]);
    const [upsertNotification, dismissNotification] = useStore(
        notificationStore,
        (state) => [state.upsertNotification, state.dismissNotification],
    );

    const searchAdvertiserId = isAccountAgnosticId()
        ? undefined
        : getAdvertiserId()!;
    const marketplaceId = getMarketplaceId()!;

    const [getContentsState, getContentsAction] = useGetUnifiedContentsAsync({
        advertiserId: searchAdvertiserId,
        marketplaceId,
    });

    const contentRequestID = useRef(uuid.v4());

    const fetchContents = useCallback(
        async (isPagination = false) => {
            const newId = uuid.v4();
            contentRequestID.current = newId;

            let curInsights = contents;
            let curLoadingStates = templateLoadingStates;
            let curCounts = templateCounts;

            if (!isPagination) {
                setContents(initialInsightTemplates);
                setTemplateLoadingStates(initialLoadingStates);
                setTemplateCounts(initialTemplateCounts);
                curInsights = initialInsightTemplates;
                curLoadingStates = initialLoadingStates;
                curCounts = initialTemplateCounts;
                pageNumber.current = 1;
            }

            // Determine which template types to load
            const templatesToLoad = Object.values(InsightDeckTemplateTheme);

            // Set loading state for all template types that will be loaded
            const updatedLoadingStates = { ...curLoadingStates };
            templatesToLoad.forEach((theme) => {
                updatedLoadingStates[theme] = "loading";
            });
            setTemplateLoadingStates(updatedLoadingStates);

            // Set last search before API response
            setLastSearchParameters();

            // Make separate API calls for each template type and update UI immediately as each completes
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            templatesToLoad.forEach(async (theme) => {
                try {
                    const result = await getContentsAction.execute(
                        pageNumber.current,
                        undefined,
                        [theme],
                    );

                    // Check if request is still valid
                    if (contentRequestID.current !== newId) return;

                    const { content, totalHits, errors } = result;

                    // Update state immediately for this specific template type
                    setContents((prevContents) => {
                        const updatedContents = { ...prevContents };
                        if (isPagination) {
                            updatedContents[theme] = [
                                ...updatedContents[theme],
                                ...content,
                            ];
                        } else {
                            updatedContents[theme] = content;
                        }
                        return updatedContents;
                    });

                    // Update count for this template type
                    setTemplateCounts((prevCounts) => ({
                        ...prevCounts,
                        [theme]: totalHits,
                    }));

                    // Set success state for this template type
                    setTemplateLoadingStates((prevStates) => ({
                        ...prevStates,
                        [theme]: "success",
                    }));

                    // Update total hits - set to totalHits for this template type (individual results)
                    setLastSearchResultTotalCount(totalHits);

                    // Handle API errors for this template type
                    if (errors.length > 0) {
                        const additionalErrors = errors.map(
                            (e) => `[${e.provider}]: ${e.errorMessage}`,
                        );
                        searchErrors.current = [
                            ...searchErrors.current,
                            ...additionalErrors,
                        ];
                    }
                } catch (error) {
                    // Check if request is still valid
                    if (contentRequestID.current !== newId) return;

                    // Set error state for this specific template type
                    setTemplateLoadingStates((prevStates) => ({
                        ...prevStates,
                        [theme]: "error",
                    }));

                    const errorMessage =
                        error instanceof Error
                            ? error.message
                            : "Unknown error";
                    searchErrors.current = [
                        ...searchErrors.current,
                        `${theme}: ${errorMessage}`,
                    ];
                }
            });

            // Increment page number for potential pagination
            pageNumber.current = pageNumber.current + 1;
        },
        [
            searchQueryV2,
            unifiedFiltersV2,
            pageNumber,
            contents,
            templateLoadingStates,
            templateCounts,
        ],
    );

    /**
     * Update the error message shown to the user when we get a new error
     */
    useEffect(() => {
        if (searchErrors.current.length) {
            upsertNotification(
                <ContentSearchErrorNotificationMessage
                    errors={searchErrors.current}
                />,
                NotificationType.ERROR,
                searchId.current,
            );
        } else {
            dismissNotification(searchId.current);
            searchId.current = uuid.v4();
        }
    }, [searchErrors.current]);

    // When we load this component, dirty our filter state
    useEffect(() => {
        setIsContentDirty(true);
        setIsMetadataDirty(true);
    }, []);

    /**
     * Trigger a refresh of the insights after 500ms of inactivity when:
     * 1. searchQuery - The user has changed the term they're searching for
     * 2. fieldSelections - The user has changed the filters they want to apply
     * 3. contentSubType - The user selected a new insight type
     */
    useDebouncedEffect(
        () => {
            const hasChanges = filterContentStateIsDirty;
            if (hasChanges) {
                searchErrors.current = [];
                void fetchContents(false).then(() => setIsContentDirty(false));

                setIsMetadataDirty(true);
            }
        },
        [filterContentStateIsDirty],
        200,
    );

    // /**
    //  * Trigger a refresh of the data when the user scrolls to the bottom of the page, max 1 per second
    //  */
    // useDebouncedEffect(
    //     () => {
    //         const totalCount = Object.values(templateCounts).reduce((sum, count) => sum + count, 0);
    //         const hasAnyLoading = Object.values(templateLoadingStates).some(status => isLoading(status));

    //         if (
    //             isLoaderInView &&
    //             !hasAnyLoading &&
    //             pageNumber.current <=
    //                 Math.ceil(totalCount / DEFAULT_PAGE_SIZE)
    //         ) {
    //             searchErrors.current = [];
    //             void fetchContents(true);
    //         }
    //     },
    //     [
    //         isLoaderInView,
    //         templateCounts,
    //         templateLoadingStates,
    //         pageNumber,
    //         DEFAULT_PAGE_SIZE,
    //     ],
    //     500,
    // );

    /**
     * Log data each time we retrieve insights
     */
    useDebouncedEffect(
        () => {
            if (isSuccess(getContentsState.status)) {
                const totalResults = Object.values(contents).flat().length;
                logInsightBrowseResults(totalResults);
            }
        },
        [contents, getContentsState.status],
        200,
    );

    return [templateLoadingStates, contents, templateCounts];
}
